// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/AwardsSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiX, FiImage, FiUpload } from 'react-icons/fi';
import api from '../../../api';

const AwardsSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const awards = websiteData?.awards || { items: [] };

  // Local states to handle typing transactions safely in isolated local memory
  const [localTitle, setLocalTitle] = useState(awards.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(awards.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(awards.displayStyle || 'grid');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showBadges: awards.showBadges !== false,
    showYears: awards.showYears !== false
  });
  const [localItems, setLocalItems] = useState(awards.items || []);

  const [newItem, setNewItem] = useState({
    name: '',
    organization: '',
    year: new Date().getFullYear(),
    description: '',
    link: ''
  });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync back from parent properties snapshot only on initialization hooks
  useEffect(() => {
    console.log('[AwardsSection] Initializing decoupled input elements from master configurations properties map.');
    const sec = websiteData?.awards || {};
    setLocalTitle(sec.sectionTitle || '');
    setLocalSubtitle(sec.sectionSubtitle || '');
    setLocalDisplayStyle(sec.displayStyle || 'grid');
    setLocalCheckboxes({
      showBadges: sec.showBadges !== false,
      showYears: sec.showYears !== false
    });
    setLocalItems(sec.items || []);
  }, [websiteData?.awards, tenantId]);

  const handleToggle = () => {
    handleInputChange('awards', 'isActive', !awards.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[AwardsSection] Updating local setup fields: Key [${field}] -> Value [${value}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => {
      const nextState = { ...prev, [key]: !prev[key] };
      console.log(`[AwardsSection] Adjusting display mask option [${key}]:`, nextState[key]);
      return nextState;
    });
  };

  const handleAddItem = () => {
    if (!newItem.name || !newItem.organization) return;
    
    const item = {
      _id: Date.now().toString(),
      name: newItem.name,
      organization: newItem.organization,
      year: Number(newItem.year) || new Date().getFullYear(),
      description: newItem.description,
      link: newItem.link,
      badge: '',
      isFeatured: false
    };
    
    const updatedItems = [...localItems, item];
    console.log('[AwardsSection] Appending a fresh recognition card layout locally:', item);
    setLocalItems(updatedItems);
    
    // Sync structural changes up instantly to protect array parsing visibility
    handleInputChange('awards', 'items', updatedItems);
    setNewItem({
      name: '',
      organization: '',
      year: new Date().getFullYear(),
      description: '',
      link: ''
    });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[AwardsSection] Removing recognition row item entry at array index:', index);
    setLocalItems(updatedItems);
    
    // Sync structural changes up instantly to protect array parsing visibility
    handleInputChange('awards', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: field === 'year' ? (value === '' ? '' : Number(value)) : value };
    
    console.log(`[AwardsSection] Live row typing track -> Row [${index}] -> Key [${field}]`);
    setLocalItems(updatedItems);
  };

  const handleBadgeUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      alert('Image size must be less than 1MB');
      event.target.value = '';
      return;
    }

    // Assign instant blob path value string layout locally to retain preview immediacy
    const localBlobPreview = URL.createObjectURL(file);
    const previewItems = [...localItems];
    previewItems[index] = { ...previewItems[index], badge: localBlobPreview };
    setLocalItems(previewItems);

    setUploading(prev => ({ ...prev, [index]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'awards');
      formData.append('itemIndex', index);
      formData.append('subField', 'badge');

      const response = await api.post(`/api/website/${tenantId}/upload/awards`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const updatedItems = [...localItems];
        const fileUrl = response.data.data?.url || response.data.data;
        console.log('[AwardsSection] Upload resolved successfully server url address string:', fileUrl);
        updatedItems[index] = { ...updatedItems[index], badge: fileUrl };
        setLocalItems(updatedItems);
        handleInputChange('awards', 'items', updatedItems);
      } else {
        alert(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Failed to upload badge');
    } finally {
      setUploading(prev => ({ ...prev, [index]: false }));
      event.target.value = '';
    }
  };

  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (typeof urlPath === 'object') {
      if (urlPath.url) return urlPath.url.startsWith('/') ? (base ? `${base}${urlPath.url}` : urlPath.url) : urlPath.url;
      if (urlPath.fileId) return base ? `${base}/api/files/tenant-website/${urlPath.fileId}` : `/api/files/tenant-website/${urlPath.fileId}`;
      return '';
    }
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const handleRemoveBadge = (index) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], badge: '' };
    setLocalItems(updatedItems);
    handleInputChange('awards', 'items', updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[AwardsSection] Committing isolated local states blocks to source configuration models.');
    
    try {
      // Synchronously match local context variables layout to global parent parameters tree data
      await handleInputChange('awards', 'sectionTitle', localTitle);
      await handleInputChange('awards', 'sectionSubtitle', localSubtitle);
      await handleInputChange('awards', 'displayStyle', localDisplayStyle);
      await handleInputChange('awards', 'showBadges', localCheckboxes.showBadges);
      await handleInputChange('awards', 'showYears', localCheckboxes.showYears);
      await handleInputChange('awards', 'items', localItems);

      const assembledPayload = {
        ...awards,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showBadges: localCheckboxes.showBadges,
        showYears: localCheckboxes.showYears,
        items: localItems
      };

      await handleSave('awards', assembledPayload);
    } catch (error) {
      console.error('Failed to save awards:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Card row toolbar context */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-990 tracking-tight">Awards & Recognitions</h2>
            <p className="text-sm text-slate-500 mt-0.5">Showcase external corporate honors, awards, and milestones achievements</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              type="button"
              onClick={handleToggle}
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                awards.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {awards.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saving}
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Core Layout Inputs configuration wrapper card */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Awards & Recognitions"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Accolades that speak to our excellence"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="grid">Grid Matrix Layout</option>
                <option value="carousel">Carousel View Slider</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Badges Graphic', key: 'showBadges', checked: localCheckboxes.showBadges },
                { label: 'Show Years Label', key: 'showYears', checked: localCheckboxes.showYears },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Award Items List Container mapping loop */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Awards</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Milestones Configured
            </span>
          </div>

          <div className="space-y-4">
            {localItems.map((item, index) => {
              const resolvedBadge = resolveAssetUrl(item.badge);

              return (
                <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="p-4 sm:p-5 space-y-4">
                    
                    {/* Inputs fields content parameter matrices layout grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Award Title Designation</span>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="Award Name"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Conabling Organization</span>
                        <input
                          type="text"
                          value={item.organization || ''}
                          onChange={(e) => handleItemChange(index, 'organization', e.target.value)}
                          placeholder="Organization"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Conferral Year Label</span>
                        <input
                          type="number"
                          value={item.year ?? ''}
                          onChange={(e) => handleItemChange(index, 'year', e.target.value)}
                          placeholder="Year"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 md:col-span-3 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Verification Link URL</span>
                        <input
                          type="text"
                          value={item.link || ''}
                          onChange={(e) => handleItemChange(index, 'link', e.target.value)}
                          placeholder="Link (optional)"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 md:col-span-3 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Short Description Outline Summary</span>
                        <input
                          type="text"
                          value={item.description || ''}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Short description summary text"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Badge Image Selector file component block parameters */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-slate-50 p-3 rounded-xl border border-slate-150">
                      <div className="flex-shrink-0 mx-auto sm:mx-0">
                        <div className="w-16 h-16 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden bg-white shadow-inner relative group">
                          {resolvedBadge ? (
                            <div className="w-full h-full p-1 relative">
                              <img src={resolvedBadge} alt={item.name || "Award Badge Graphics Preview"} className="w-full h-full object-contain" />
                              <button
                                type="button"
                                onClick={() => handleRemoveBadge(index)}
                                className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-xl"
                              >
                                <FiX className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <FiImage className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                      </div>
                      <div className="text-center sm:text-left flex-1">
                        <input
                          type="file"
                          id={`award-badge-${index}`}
                          accept="image/jpeg,image/png,image/webp,image/svg+xml"
                          onChange={(e) => handleBadgeUpload(e, index)}
                          disabled={uploading[index]}
                          className="hidden"
                        />
                        <label
                          htmlFor={`award-badge-${index}`}
                          className={`inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 shadow-sm cursor-pointer transition-all select-none ${
                            uploading[index] ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                          }`}
                        >
                          <FiUpload className="mr-1.5 h-3.5 w-3.5 text-slate-500" />
                          {uploading[index] ? 'Uploading...' : (resolvedBadge ? 'Change Badge Graphic' : 'Upload Badge Graphic')}
                        </label>
                        <p className="text-[10px] text-slate-400 mt-1">Accepts JPG, PNG, WEBP, SVG under 1MB</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-slate-100">
                      <label className="flex items-center space-x-2 cursor-pointer group select-none">
                        <input
                          type="checkbox"
                          checked={item.isFeatured || false}
                          onChange={(e) => handleItemChange(index, 'isFeatured', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                          item.isFeatured ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-xs bg-white ${item.isFeatured ? 'block' : 'hidden'}`} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800">Featured Highlight Entry</span>
                      </label>

                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors"
                      >
                        <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Award
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add Entry Action Subform card alignments layout setup */}
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1 bg-blue-50 rounded-md text-blue-600">
              <FiPlus className="h-4 w-4 stroke-[3]" />
            </div>
            <h4 className="text-sm font-bold text-slate-800">Create New Award Card</h4>
          </div>
          
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="Award Name *"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.organization}
                onChange={(e) => setNewItem({ ...newItem, organization: e.target.value })}
                placeholder="Organization *"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="number"
                value={newItem.year}
                onChange={(e) => setNewItem({ ...newItem, year: Number(e.target.value) })}
                placeholder="Year"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row items-end gap-3">
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Short description summary text content details..."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <button
                type="button"
                onClick={handleAddItem}
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
              >
                Insert Award
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AwardsSection;